import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, contentChild, inject, input, TemplateRef } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { isObservable, Observable, take } from 'rxjs';

/**
 * Configuration for the Vectronic dialog.
 */
export type VectronicDialogConfig = {
  /**
   * The title of the dialog. Ignored when a `#header` template is projected instead.
   */
  title?: string;
  /**
   * Whether to hide the close button.
   * @default false
   */
  hideCloseButton?: boolean;
  /**
   * Guard invoked when the close (X) icon is clicked. Return (or emit) `false` to keep the
   * dialog open, e.g. to confirm discarding unsaved changes. When omitted, the close icon
   * closes the dialog immediately.
   */
  beforeClose?: () => boolean | Observable<boolean>;
};

/**
 * A reusable dialog component with a consistent Vectronic style.
 *
 * @example
 * ```html
 * <vectronic-dialog [config]="{ title: 'My Dialog' }">
 *   <div body>
 *     <p>This is the content of the dialog.</p>
 *   </div>
 *   <div footer>
 *     <button mat-button>Cancel</button>
 *   </div>
 * </vectronic-dialog>
 * ```
 *
 * For the rare case where the title bar itself needs custom content (a badge, a
 * secondary identifier styled differently from the title, …), project a `#header`
 * template instead of setting `config.title` — the close button still renders and
 * still goes through `beforeClose`, only the title area is replaced:
 * ```html
 * <vectronic-dialog [config]="{ beforeClose: confirmClose }">
 *   <ng-template #header>
 *     <h1>My Dialog</h1>
 *     <span class="my-id">#1234</span>
 *   </ng-template>
 *   <div body>...</div>
 * </vectronic-dialog>
 * ```
 */
@Component({
  imports: [MatIcon, NgTemplateOutlet],
  selector: 'vectronic-dialog',
  template: `
    <div class="container">
      <section class="header">
        <div class="header-title">
          @if (headerTemplate(); as header) {
            <ng-container [ngTemplateOutlet]="header"></ng-container>
          } @else {
            <h1>{{ resultConfig().title }}</h1>
          }
        </div>
        <mat-icon [class.hidden]="resultConfig().hideCloseButton" (click)="onCloseClick()"
          >close</mat-icon
        >
      </section>
      <section class="body">
        <ng-content select="[body]"></ng-content>
      </section>
      <section class="footer">
        <ng-content select="[footer]"></ng-content>
      </section>
    </div>
  `,
  styles: `
    @use 'variables';
    :host {
      display: block;
      height: 100%;
      box-sizing: border-box;
    }
    .container {
      padding: 0 1rem 0.5rem 1rem;
      height: 100%;
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
    }
    .header {
      display: flex;
      justify-content: space-between;
      place-items: center;
      gap: 0.75rem;
      position: sticky;
      top: 0;
      background-color: white;
      height: 3.5rem;
      flex-shrink: 0;
      z-index: 2;
      border-bottom: 1.7px solid rgba(0, 0, 0, 0.125);

      mat-icon {
        flex-shrink: 0;
        cursor: pointer;
      }
      .header-title {
        display: flex;
        align-items: center;
        gap: 0.625rem;
        flex: 1;
        min-width: 0;
      }
      h1 {
        font-size: 1.25rem;
        color: variables.$vectronic-secondary;
        font-weight: 500;
      }
    }
    .body {
      flex: 1;
      overflow: auto;
      padding-top: 0.75rem;
      padding-bottom: 0.75rem;
    }
    .footer {
      flex-shrink: 0;
      padding-top: 0.75rem;
      border-top: 1.7px solid rgba(0, 0, 0, 0.125);
    }
    .footer:empty {
      display: none;
    }
  `,
})
export class VectronicDialogComponent {
  private dialogRef = inject(MatDialogRef, { optional: true });
  /**
   * The default configuration for the dialog.
   * @private
   */
  private readonly defaultConfig: Pick<VectronicDialogConfig, 'title' | 'hideCloseButton'> = {
    title: 'Title',
    hideCloseButton: false,
  };
  /**
   * The configuration for the dialog.
   */
  config = input.required<VectronicDialogConfig>();
  /**
   * Custom title-bar content, projected as `<ng-template #header>`. Takes over the title
   * area in place of `config.title`; the close button is unaffected.
   */
  protected headerTemplate = contentChild<TemplateRef<unknown>>('header');
  /**
   * The resulting configuration, merging the provided config with the default config.
   * @protected
   */
  protected resultConfig = computed(() => ({
    ...this.defaultConfig,
    ...this.config(),
  }));

  protected onCloseClick(): void {
    const guard = this.resultConfig().beforeClose;
    if (!guard) {
      this.dialogRef?.close();
      return;
    }
    const result = guard();
    if (isObservable(result)) {
      result.pipe(take(1)).subscribe((canClose) => {
        if (canClose) this.dialogRef?.close();
      });
    } else if (result) {
      this.dialogRef?.close();
    }
  }
}
